import React, {
  memo,
  useMemo,
  useState,
  useContext,
  PropsWithChildren,
} from 'react';
import {
  Button,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Nav,
  NavLink,
  NavbarBrand,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ButtonDropdown,
} from 'reactstrap';
import Sidenav from './Sidenav';
import LocalImage from '../common/images/LocalImage';
import {
  FaBars,
  FaCogs,
  FaComment,
  FaFileAlt,
  FaHome,
  FaSearch,
} from 'react-icons/fa';
import { isStaffType, UserContext, UserType } from '../../context/UserContext';
import {
  rootPath,
  toAccountSettings,
  toBrowseJobs,
  toConversationList,
  toDashboard,
  toJobSearchResults,
  toProfile,
  toSignOut,
} from '../../routes';
import { Link } from 'gatsby';
import ProfilePicture from '../common/ProfilePicture';
import { positionCodeToDisplayName } from '../../utils/displayHelper';
import UnreadMessages from '../messages/UnreadMessages';
import LegacyProfileSetupModal from '../common/forms/LegacyProfileSetupModal';
import PromoPrompt from '../promo/PromoPrompt';

const ICON_HOME = <FaHome className="fa-lg" />;
const ICON_SEARCH = <FaSearch className="fa-lg" />;
const ICON_MESSAGES = <FaComment className="fa-lg" />;
const ICON_PROFILE = <FaFileAlt className="fa-lg" />;
const ICON_SETTINGS = <FaCogs className="fa-lg" />;
const ICON_BARS = <FaBars className="dp-font-lg" />;

const PROFILE_PIC_STYLE = { height: '55px', width: '55px' };
const BRAND_STYLE = { height: '65px' };
const COLOR_STRIPE_STYLE = { minHeight: '5px' };
const UNREAD_BADGE_STYLE = {
  position: 'absolute' as const,
  top: 0,
  right: '1rem',
};

const colorStripe = (
  <div style={COLOR_STRIPE_STYLE} className="mt-auto d-flex d-lg-none w-100">
    <div className="bg-primary col-4"></div>
    <div className="bg-success col-4"></div>
    <div className="bg-secondary col-4"></div>
  </div>
);

const UserDetails = memo(function UserDetails({
  user,
  name,
}: {
  user: UserType | null;
  name: string;
}) {
  return (
    <div className="d-flex gap-2 align-items-center px-3">
      <div style={PROFILE_PIC_STYLE}>
        <ProfilePicture
          photoUrl={user?.photoUrl}
          alt={name}
          className="border-0 bg-info"
          resumePreview
        />
      </div>
      <div className="text-left d-none d-md-block">
        <p className="lead fw-bold mb-0">{name}</p>
        <p className="mb-0">
          {isStaffType(user) ? positionCodeToDisplayName[user.position] : ''}
        </p>
      </div>
    </div>
  );
});

const StaffLayout = (props: PropsWithChildren<{ pathname: string }>) => {
  const { user } = useContext(UserContext);
  const [offcanvasIsOpen, setOffcanvasIsOpen] = useState(false);
  const [userMenuIsOpen, setUserMenuIsOpen] = useState(false);

  const name =
    isStaffType(user) && !!user.firstName
      ? `${user.firstName} ${user.lastName}`
      : (user?.name ?? '...Loading User');

  function toggleUserMenu() {
    setUserMenuIsOpen((prev) => !prev);
  }

  function toggleOffcanvas() {
    setOffcanvasIsOpen((prev) => !prev);
  }

  const navLinks = useMemo(
    () => [
      { route: toDashboard, label: 'Home', icon: ICON_HOME },
      {
        route: isStaffType(user)
          ? toJobSearchResults(user.position, user.zip)
          : toBrowseJobs,
        label: 'Jobs',
        icon: ICON_SEARCH,
      },
      { route: toConversationList, label: 'Messages', icon: ICON_MESSAGES },
      { route: toProfile, label: 'Profile', icon: ICON_PROFILE },
      { route: toAccountSettings, label: 'Settings', icon: ICON_SETTINGS },
    ],
    [user]
  );

  return (
    <div className="h-100 bg-off-white">
      <div className="bg-white d-flex flex-column m-auto h-100">
        <PromoPrompt mode="banner" />
        <header
          id="staff-header"
          className={
            'w-100 border-bottom bg-white border-success d-flex justify-content-between align-items-center py-2 px-3'
          }
        >
          <div className="d-flex gap-2 align-items-center justify-content-between w-100">
            <NavbarBrand
              tag={Link}
              to={rootPath}
              style={BRAND_STYLE}
              className="d-flex align-items-center"
            >
              <LocalImage
                src="dentalpost-logo-on-light-bg.png"
                alt="DentalPost Logo"
                height={44}
                width={70}
                loading="eager"
              />
            </NavbarBrand>
            <ButtonDropdown
              isOpen={userMenuIsOpen}
              toggle={toggleUserMenu}
              className="ms-auto"
            >
              <DropdownToggle
                color="link"
                caret
                className="d-flex align-items-center text-black"
              >
                <UserDetails user={user} name={name} />
              </DropdownToggle>
              <DropdownMenu className="w-100">
                <DropdownItem tag={Link} to={toAccountSettings}>
                  Settings
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem tag={Link} to={toSignOut}>
                  Sign Out
                </DropdownItem>
              </DropdownMenu>
            </ButtonDropdown>{' '}
            <Button
              color="link"
              onClick={toggleOffcanvas}
              aria-label="Main Menu"
              className="rounded d-lg-none"
            >
              {ICON_BARS}
            </Button>
            <Offcanvas
              direction="end"
              isOpen={offcanvasIsOpen}
              toggle={toggleOffcanvas}
              id="sidenav-offcanvas"
            >
              <OffcanvasHeader toggle={toggleOffcanvas}></OffcanvasHeader>
              <OffcanvasBody className="bg-solids-off-white-50 px-0 pt-0">
                <Sidenav toggle={toggleOffcanvas} />
              </OffcanvasBody>
            </Offcanvas>{' '}
          </div>
        </header>
        <main
          role="main"
          className="align-self-stretch flex-grow-1 overflow-hidden bg-solids-white d-flex w-100"
        >
          <div className="d-none d-lg-block h-100 border-end">
            <Sidenav />
          </div>
          <div id="main-content" className="h-100 overflow-auto flex-grow-1">
            <div className="flex-grow-1 dp-mobile-container d-lg-block">
              {props.children}
            </div>
          </div>
        </main>
        {colorStripe}{' '}
        <div className="dp-mobile-container">
          <Nav
            id="staff-footer"
            className="staff-nav d-lg-none d-flex justify-content-around align-items-center px-2 py-1 border-top bg-white"
          >
            {navLinks.map((link) => {
              return (
                <NavLink
                  key={link.route}
                  tag={Link}
                  to={link.route}
                  active={props.pathname.includes(link.route)}
                  className="d-flex flex-grow-1 col-2 justify-content-center align-items-center flex-column rounded position-relative mx-1"
                >
                  {link.icon}
                  {link.label === 'Messages' && (
                    <div style={UNREAD_BADGE_STYLE}>
                      <UnreadMessages />
                    </div>
                  )}
                  <span>{link.label}</span>
                </NavLink>
              );
            })}
          </Nav>
        </div>
      </div>
      <LegacyProfileSetupModal />
    </div>
  );
};

export default StaffLayout;
